import React, { useState, useEffect } from 'react'
import classnames from 'classnames'
import { renderToString } from 'react-dom/server'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import { useFormatMessage } from 'src/providers/intl'
import { isEmpty, get, range } from 'lodash'
import { CheckMarkSvg, AlertSvg } from 'src/svgs'

// Hooks
import { useTrackHomeEventOnce } from 'src/hooks'

// Selectors
import { selectValuationBounds } from 'src/store/selectors/home'
import {
  selectCELsPaymentForExistingLoans,
  selectCELsOutstandingPrincipal,
} from 'src/store/selectors/home/loans'
import { selectThirtyYearFixedRate } from 'src/store/selectors/refinance'
import {
  selectRecentSoldDate,
  selectPercentDown,
} from 'src/store/selectors/shouldYouSell'

// Utils
import {
  generateMortgagePaymentDifference,
  pocketedCashAmount,
  calculateSellingFees,
} from 'src/helpers/refinance'
import { roundToNearestThousand } from 'src/helpers/utils'
import { oneTimeTagEventDispatcher } from 'src/helpers/tagManager'

// Constants
import { QUESTIONS } from 'src/constants/messages'
import { HOMEOWNER_CASH_POSITION_TOPIC_KEY } from 'src/constants/topicKeys'
import { MOVED_CASH_POSITION_SLIDER } from 'src/constants/client/events'
import { MovedShouldYouSellCashPositionSlider } from 'src/constants/trackingEvents'
import {
  ABBREVIATION_BREAKPOINT,
  LOW_RANGE_PERCENT_DOWN,
  LOWER_STEP_AMOUNT,
  NEW_HOME_MIN_PRICE,
  PERCENT_DOWN_INCREMENTS,
  STEP_VALUE_BREAKPOINT,
  UPPER_NEW_HOME_PRICE_PERCENT,
  UPPER_RANGE_PERCENT_DOWN,
  UPPER_STEP_AMOUNT,
} from 'src/constants/shouldYouSell'

// Components
import QuestionSingle from 'src/Components/shared/Questions/QuestionSingleContainer'
import { Slider, Text, If, Unless, Select } from 'src/Components'
import Format from 'src/Components/shared/Format'
import SoldDateDisclaimer from '../SoldDateDisclaimer'

// Styles
import styles from './Body.module.scss'

const sliderCurrencyPropTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

const SliderFormat = ({ value }) => <Format.Currency abbreviate value={value} />

SliderFormat.propTypes = sliderCurrencyPropTypes

const bodyPropTypes = {
  hidden: PropTypes.bool,
}

const Body = ({ hidden = false }) => {
  const intlFormat = useFormatMessage()
  const {
    homebotAmount: initialSellingPrice,
    high,
    low,
  } = useSelector(selectValuationBounds)

  const currentLoanPayment = useSelector(selectCELsPaymentForExistingLoans)
  const outstandingPrincipal = useSelector(selectCELsOutstandingPrincipal)
  const thirtyYearFixedRate = useSelector(selectThirtyYearFixedRate)
  const recentSoldDate = useSelector(selectRecentSoldDate)
  const initialPercentDown = useSelector(selectPercentDown)

  const calculateInitialNewHomePrice = value => {
    // eslint-disable-next-line no-shadow
    const newHomePrice = roundToNearestThousand(value * 0.9)

    return newHomePrice > NEW_HOME_MIN_PRICE ? newHomePrice : NEW_HOME_MIN_PRICE
  }

  const trackSliderEvent = useTrackHomeEventOnce(MOVED_CASH_POSITION_SLIDER)

  // Rounded Selling and New Home Prices
  const roundedInitialSellingPrice = roundToNearestThousand(initialSellingPrice)
  const roundedInitialNewHomePrice =
    calculateInitialNewHomePrice(initialSellingPrice)
  const maxNewHomePrice =
    roundedInitialSellingPrice * UPPER_NEW_HOME_PRICE_PERCENT

  // Prices In State
  const [sellingPrice, setSellingPrice] = useState(roundedInitialSellingPrice)
  const [newHomePrice, setNewHomePrice] = useState(roundedInitialNewHomePrice)
  const [monthlyPaymentDiff, setMonthlyPaymentDiff] = useState(0)
  const [pocketAmount, setPocketAmount] = useState(0)
  const [percentDown, setPercentDown] = useState(initialPercentDown)
  const [sellingFees, setSellingFees] = useState(0)
  const [netAmount, setNetAmount] = useState(0)

  // Abbreviation Currency Helpers
  const absoluteMonthlyPaymentDiff = Math.abs(monthlyPaymentDiff)
  const absolutePocketedAmount = Math.abs(pocketAmount)
  const absoluteSellingFees = Math.abs(sellingFees)
  const absoluteNetAmount = Math.abs(netAmount)
  const abbreviateMonthlyPaymentDiff =
    absoluteMonthlyPaymentDiff >= ABBREVIATION_BREAKPOINT
  const abbreviatePocketedAmount =
    absolutePocketedAmount >= ABBREVIATION_BREAKPOINT
  const abbreviateSellingAmount = absoluteSellingFees >= ABBREVIATION_BREAKPOINT
  const abbreviateNetAmount = absoluteNetAmount >= ABBREVIATION_BREAKPOINT

  // Trigger for Good or Bad Amounts
  const goodMonthlyPaymentDiff = monthlyPaymentDiff <= 0
  const goodPocketedAmount = pocketAmount > 0

  // Formulas for Determining step amounts for sliders
  const sellingStepAmount =
    initialSellingPrice >= STEP_VALUE_BREAKPOINT
      ? UPPER_STEP_AMOUNT
      : LOWER_STEP_AMOUNT

  const newHomeStepAmount = () => {
    const delta = Math.abs(maxNewHomePrice - NEW_HOME_MIN_PRICE)

    if (delta > 800000) {
      return 20000
    }
    if (delta <= 800000 && delta > 500000) return 10000

    return 5000
  }

  const incrementsOfFive = () =>
    range(LOW_RANGE_PERCENT_DOWN, UPPER_RANGE_PERCENT_DOWN).filter(
      num => num % PERCENT_DOWN_INCREMENTS === 0
    )

  const renderPercentValue = ({ value }) => {
    const formattedValue = (
      <Format.Currency abbreviate value={(newHomePrice * value) / 100} />
    )

    return `${renderToString(formattedValue)} (${value}%)`
  }

  const defaultOptions = () => {
    const options = incrementsOfFive().map(number => ({
      key: number,
      value: number,
    }))

    return options
  }

  useEffect(() => {
    // Guard against async loading of rates
    if (isEmpty(thirtyYearFixedRate)) {
      return
    }

    // Reset Values on closing
    const salePriceOfCurrentHome = hidden
      ? roundedInitialSellingPrice
      : sellingPrice
    const purchasePriceOfNewHome = hidden
      ? roundedInitialNewHomePrice
      : newHomePrice

    const adjustedEquity = salePriceOfCurrentHome - outstandingPrincipal

    if (hidden) {
      setSellingPrice(roundedInitialSellingPrice)
      setNewHomePrice(roundedInitialNewHomePrice)
      setPercentDown(initialPercentDown)
    }

    // Calculate Increase or Decrease in new mortgage pmt from old
    const diff = generateMortgagePaymentDifference({
      rate: thirtyYearFixedRate,
      loanBalance: (1 - percentDown / 100) * purchasePriceOfNewHome,
      currentLoanPayment,
    })

    setMonthlyPaymentDiff(Math.round(diff))

    // Calculate pocketed amount after sale of current home and purchase of new (with 20% down)
    const pocketedAmount = pocketedCashAmount({
      priceOfNewHouse: purchasePriceOfNewHome,
      equity: adjustedEquity,
      homeValue: salePriceOfCurrentHome,
      percentDown: percentDown / 100,
    })

    const totalSellingFees = calculateSellingFees(salePriceOfCurrentHome)
    const totalNetAmount = adjustedEquity - totalSellingFees

    setPocketAmount(Math.round(pocketedAmount))
    setSellingFees(Math.round(totalSellingFees))
    setNetAmount(Math.round(totalNetAmount))
  }, [
    currentLoanPayment,
    hidden,
    initialPercentDown,
    newHomePrice,
    outstandingPrincipal,
    percentDown,
    roundedInitialSellingPrice,
    roundedInitialNewHomePrice,
    sellingPrice,
    thirtyYearFixedRate,
  ])

  return (
    <div>
      <Text
        className={styles.cta}
        element='div'
        size='l'
        variant='neutralCool'
        variantCount='900'
      >
        <FormattedMessage
          id='ShouldYouSell.cashPosition.good.body.ctaOne'
          defaultMessage='If you sold your home for'
        />
      </Text>

      <Slider
        className={styles.slider}
        name='soldHomeSlider'
        value={sellingPrice}
        onChange={e => {
          setSellingPrice(roundToNearestThousand(e.target.value))
          trackSliderEvent()
          oneTimeTagEventDispatcher(MovedShouldYouSellCashPositionSlider)
        }}
        step={sellingStepAmount}
        min={low}
        max={high}
        valueFormat={SliderFormat}
      />
      <Text
        className={styles.subText}
        weight='semibold'
        variant='neutralCool'
        variantCount='700'
      >
        <FormattedMessage
          id='ShouldYouSell.cashPosition.good.body.subText'
          defaultMessage="you'd pay around {sellingFees} in fees and net around {netAmount} in cash."
          values={{
            sellingFees: (
              <Format.Currency
                abbreviate={abbreviateSellingAmount}
                value={absoluteSellingFees}
              />
            ),
            netAmount: (
              <Text element='span' weight='semibold' variant='success'>
                <Format.Currency
                  abbreviate={abbreviateNetAmount}
                  value={absoluteNetAmount}
                />
              </Text>
            ),
          }}
        />
      </Text>

      <div className={styles.separator} />

      <Text
        className={styles.cta}
        element='div'
        size='l'
        variant='neutralCool'
        variantCount='900'
      >
        <FormattedMessage
          id='ShouldYouSell.cashPosition.good.body.ctaTwo'
          defaultMessage='Then, you could buy a home for'
        />
      </Text>

      <Slider
        className={styles.slider}
        name='newHomeSlider'
        value={newHomePrice}
        onChange={e => {
          setNewHomePrice(roundToNearestThousand(e.target.value))
          trackSliderEvent()
          oneTimeTagEventDispatcher(MovedShouldYouSellCashPositionSlider)
        }}
        step={newHomeStepAmount()}
        min={NEW_HOME_MIN_PRICE}
        max={maxNewHomePrice}
        valueFormat={SliderFormat}
      />

      <Text
        className={styles.subText}
        element='div'
        weight='semibold'
        variant='neutralCool'
        variantCount='700'
      >
        <FormattedMessage
          id='ShouldYouSell.cashPosition.good.body.ctaThree'
          defaultMessage='with a {select} downpayment.'
          values={{
            select: (
              <Select
                className={styles.select}
                aria-label='Choose percent down'
                onChange={value => {
                  setPercentDown(parseFloat(value))
                }}
                value={percentDown}
                optionValue={renderPercentValue}
                options={defaultOptions()}
              >
                <option className='Select_option__1d7KH' value='5'>
                  5%
                </option>
              </Select>
            ),
          }}
        />
      </Text>

      <div className={styles.separator} />

      <Text
        className={styles.cta}
        element='div'
        size='l'
        variant='neutralCool'
        variantCount='900'
      >
        <FormattedMessage
          id='ShouldYouSell.cashPosition.good.body.ctaFour'
          defaultMessage='In this scenario, you might:'
        />
      </Text>
      <div className={styles.results}>
        <div>
          <If condition={goodMonthlyPaymentDiff}>
            <Text
              weight='semibold'
              element='div'
              className={classnames(styles.infoWrapper, styles.infoGood)}
            >
              <div className={styles.checkMarkSvgWrapper}>
                <CheckMarkSvg />
              </div>

              <div className={styles.infoText}>
                <FormattedMessage
                  id='ShouldYouSell.cashPosition.good.body.saving'
                  defaultMessage='Save around'
                />{' '}
                <Text element='span' weight='semibold' variant='success'>
                  <Format.Currency
                    abbreviate={abbreviateMonthlyPaymentDiff}
                    value={absoluteMonthlyPaymentDiff}
                  />{' '}
                  <FormattedMessage
                    id='ShouldYouSell.cashPosition.good.body.month'
                    defaultMessage='each month'
                  />
                </Text>
              </div>
            </Text>
          </If>
          <Unless condition={goodMonthlyPaymentDiff}>
            <Text
              weight='semibold'
              element='div'
              className={classnames(styles.infoWrapper, styles.infoBad)}
            >
              <div className={styles.alertSvgWrapper}>
                <AlertSvg className={styles.alertSvg} />
              </div>
              <div className={styles.infoText}>
                <Text element='span' variant='neutralCool' variantCount='900'>
                  <FormattedMessage
                    id='ShouldYouSell.cashPosition.good.body.paying'
                    defaultMessage='Pay about'
                  />{' '}
                </Text>
                <Text
                  element='span'
                  weight='semibold'
                  variant='warning'
                  variantCount='600'
                >
                  <Format.Currency
                    abbreviate={abbreviateMonthlyPaymentDiff}
                    value={absoluteMonthlyPaymentDiff}
                  />{' '}
                  <FormattedMessage
                    id='ShouldYouSell.cashPosition.good.body.monthMore'
                    defaultMessage='more each month'
                  />
                </Text>
              </div>
            </Text>
          </Unless>
        </div>

        <div>
          <If condition={goodPocketedAmount}>
            <Text
              weight='semibold'
              element='div'
              className={classnames(styles.infoWrapper, styles.infoGood)}
            >
              <div className={styles.checkMarkSvgWrapper}>
                <CheckMarkSvg />
              </div>

              <div className={styles.infoText}>
                <Text element='span' variant='neutralCool' variantCount='900'>
                  <FormattedMessage
                    id='ShouldYouSell.cashPosition.good.body.pocket'
                    defaultMessage='Pocket an extra {pocketAmount}'
                    values={{
                      pocketAmount: (
                        <Text
                          element='span'
                          weight='semibold'
                          variant='success'
                        >
                          <Format.Currency
                            abbreviate={abbreviatePocketedAmount}
                            value={absolutePocketedAmount}
                          />
                        </Text>
                      ),
                    }}
                  />{' '}
                </Text>
                <Text element='span' weight='semibold' variant='success'>
                  <FormattedMessage
                    id='ShouldYouSell.cashPosition.good.body.ofCash'
                    defaultMessage='of cash'
                  />
                </Text>
              </div>
            </Text>
          </If>
          <Unless condition={goodPocketedAmount}>
            <Text
              weight='semibold'
              element='div'
              className={classnames(styles.infoWrapper, styles.infoBad)}
            >
              <div className={styles.alertSvgWrapper}>
                <AlertSvg className={styles.alertSvg} />
              </div>
              <div className={styles.infoText}>
                <FormattedMessage
                  id='ShouldYouSell.cashPosition.good.body.need'
                  defaultMessage='Need an extra'
                />{' '}
                <Text element='span' weight='semibold' variant='warning'>
                  <Format.Currency
                    abbreviate={abbreviatePocketedAmount}
                    value={absolutePocketedAmount}
                  />{' '}
                  <FormattedMessage
                    id='ShouldYouSell.cashPosition.good.body.down'
                    defaultMessage='to put down'
                  />
                </Text>
              </div>
            </Text>
          </Unless>
        </div>
      </div>

      <Text
        className={styles.disclaimer}
        variant='neutralCool'
        variantCount='700'
        size='xxs'
      >
        <FormattedMessage
          id='ShouldYouSell.cashPosition.good.body.rateExplanation'
          defaultMessage='Assuming a 30-year fixed loan at {rate}%'
          values={{
            rate: get(thirtyYearFixedRate, 'standardizedRate'),
          }}
        />
      </Text>

      <If condition={recentSoldDate}>
        <div className={styles.soldDateDisclaimer}>
          <SoldDateDisclaimer />
        </div>
      </If>
      <div className={styles.question}>
        <QuestionSingle
          questionTopic={intlFormat(QUESTIONS.cashPositionTopic)}
          questionTopicKey={HOMEOWNER_CASH_POSITION_TOPIC_KEY}
          placeholder={intlFormat(QUESTIONS.goodCashPosition)}
        />
      </div>
    </div>
  )
}

Body.propTypes = bodyPropTypes
export default Body
